import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';
import { isAdmin } from '../utils/admin';
import { formatDateOnly, formatTimeOnly, formatExpireDate, getTodayPST, PST_TZ } from '../utils/date';

const TimeSlots = ({ user }) => {
  const defaultCreateFormData = {
    title: '',
    description: '',
    date: '',
    start_time: '19:00',
    end_time: '21:30',
    max_participants: 10,
    repeat_count: 1,
    repeat_interval: 'weekly'
  };

  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [packageInfo, setPackageInfo] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSlotId, setEditingSlotId] = useState(null);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    date: '',
    start_time: '',
    end_time: '',
    max_participants: 10
  });
  const [formData, setFormData] = useState(defaultCreateFormData);

  useEffect(() => {
    fetchSlots();
  }, []);

  useEffect(() => {
    if (slots.length === 0) {
      setSelectedSlotId(null);
      setEditingSlotId(null);
      return;
    }
    if (selectedSlotId != null && !slots.some((slot) => slot.id === selectedSlotId)) {
      setSelectedSlotId(null);
      setEditingSlotId(null);
    }
  }, [slots, selectedSlotId]);

  useEffect(() => {
    if (user && !isAdmin(user)) {
      api.get('/timeslots/user/package')
        .then((res) => setPackageInfo(res.data))
        .catch(() => setPackageInfo(null));
    } else {
      setPackageInfo(null);
    }
  }, [user, slots]);

  const fetchSlots = async () => {
    try {
      const response = await api.get('/timeslots');
      setSlots(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching classes:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load classes';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async (slot) => {
    if (!user) {
      alert('Please login to check in');
      return;
    }
    const title = slot.title || 'this class';
    const dateStr = slot.start_time ? formatDateOnly(slot.start_time) : '';
    if (!window.confirm(`Check in to ${title}${dateStr ? ` (${dateStr})` : ''}? This will use 1 credit.`)) {
      return;
    }
    try {
      await api.post(`/timeslots/${slot.id}/checkin`);
      alert('Successfully checked in!');
      fetchSlots();
      if (user && !isAdmin(user)) {
        const pkg = await api.get('/timeslots/user/package');
        setPackageInfo(pkg.data);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to check in');
    }
  };

  const handleCancelCheckIn = async (slotId) => {
    if (!user) return;
    try {
      await api.delete(`/timeslots/${slotId}/checkin`);
      alert('Check-in cancelled.');
      fetchSlots();
      if (user && !isAdmin(user)) {
        const pkg = await api.get('/timeslots/user/package');
        setPackageInfo(pkg.data);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to cancel check-in');
    }
  };

  const isMoreThan24hBefore = (startTime) => {
    if (!startTime) return false;
    const start = new Date(startTime).getTime();
    return start - Date.now() > 24 * 60 * 60 * 1000;
  };

  const addDays = (dateStr, days) => {
    const d = new Date(dateStr + 'T12:00:00');
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  };

  const handleCreateSlot = async (e) => {
    e.preventDefault();
    try {
      const MAX_REPEAT_DAYS = 92; // ~3 months
      const intervalDays = formData.repeat_interval === 'daily' ? 1 : 7;
      const maxCount = 1 + Math.floor(MAX_REPEAT_DAYS / intervalDays);
      const count = Math.min(maxCount, Math.max(1, parseInt(formData.repeat_count, 10) || 1));
      for (let i = 0; i < count; i++) {
        const date = addDays(formData.date, i * intervalDays);
        const startDateTime = `${date}T${formData.start_time}`;
        const endDateTime = `${date}T${formData.end_time}`;
        await api.post('/timeslots', {
          title: formData.title,
          description: formData.description,
          start_time: startDateTime,
          end_time: endDateTime,
          max_participants: formData.max_participants
        });
      }
      alert(count === 1 ? 'Class created successfully!' : `${count} classes created successfully!`);
      setShowCreateForm(false);
      setFormData(defaultCreateFormData);
      fetchSlots();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create class');
    }
  };

  const toDateTimeLocal = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };

  const startEdit = (slot) => {
    const startDt = toDateTimeLocal(slot.start_time);
    const endDt = toDateTimeLocal(slot.end_time);
    setEditingSlotId(slot.id);
    setEditFormData({
      title: slot.title,
      description: slot.description || '',
      date: startDt ? startDt.slice(0, 10) : '',
      start_time: startDt ? startDt.slice(11, 16) : '',
      end_time: endDt ? endDt.slice(11, 16) : '',
      max_participants: slot.max_participants || 10
    });
  };

  const cancelEdit = () => {
    setEditingSlotId(null);
  };

  const handleUpdateSlot = async (e) => {
    e.preventDefault();
    try {
      const startDateTime = `${editFormData.date}T${editFormData.start_time}`;
      const endDateTime = `${editFormData.date}T${editFormData.end_time}`;
      await api.put(`/timeslots/${editingSlotId}`, {
        title: editFormData.title,
        description: editFormData.description,
        start_time: startDateTime,
        end_time: endDateTime,
        max_participants: editFormData.max_participants
      });
      alert('Class updated successfully!');
      setEditingSlotId(null);
      fetchSlots();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update class');
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm('Delete this class? This cannot be undone.')) return;
    try {
      await api.delete(`/timeslots/${slotId}`);
      if (selectedSlotId === slotId) {
        setSelectedSlotId(null);
        setEditingSlotId(null);
      }
      fetchSlots();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete class');
    }
  };


  const slotRowStyle = { marginBottom: '18px' };
  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const toPSTDateKey = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-CA', { timeZone: PST_TZ });
  };

  const toCalendarDateKey = (year, month, dayOfMonth) => {
    const noonUtc = new Date(Date.UTC(year, month, dayOfMonth, 12, 0, 0));
    return noonUtc.toLocaleDateString('en-CA', { timeZone: PST_TZ });
  };

  const slotsByDate = useMemo(() => {
    const grouped = {};
    slots.forEach((slot) => {
      const key = toPSTDateKey(slot.start_time);
      if (!key) return;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(slot);
    });
    Object.values(grouped).forEach((items) => {
      items.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
    });
    return grouped;
  }, [slots]);

  const monthYearLabel = calendarMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const monthStartDay = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1).getDay();
  const monthDaysCount = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0).getDate();
  const visibleMonthPrefix = `${calendarMonth.getFullYear()}-${String(calendarMonth.getMonth() + 1).padStart(2, '0')}`;
  const hasClassesInVisibleMonth = Object.keys(slotsByDate).some((key) => key.startsWith(visibleMonthPrefix));

  const handlePickSlotFromCalendar = (slotId) => {
    setSelectedSlotId(slotId);
    setEditingSlotId(null);
  };

  const goToPrevMonth = () => {
    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const goToCurrentMonth = () => {
    const now = new Date();
    setCalendarMonth(new Date(now.getFullYear(), now.getMonth(), 1));
  };
  const selectedSlot = selectedSlotId == null ? null : slots.find((slot) => slot.id === selectedSlotId) || null;

  if (loading) {
    return <div>Loading classes...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Classes</h1>
        {user && isAdmin(user) && (
          <button className="btn btn-primary" onClick={() => setShowCreateForm(!showCreateForm)}>
            {showCreateForm ? 'Cancel' : 'Create Class'}
          </button>
        )}
      </div>

      {error && <div style={{ color: 'red', marginBottom: '20px' }}>{error}</div>}

      {showCreateForm && user && isAdmin(user) && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h2>Create New Class</h2>
          <form onSubmit={handleCreateSlot}>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Date *</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Start time *</label>
              <input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>End time *</label>
              <input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Max Participants</label>
              <input
                type="number"
                min="1"
                value={formData.max_participants}
                onChange={(e) => setFormData({ ...formData, max_participants: parseInt(e.target.value) })}
              />
            </div>
            {isAdmin(user) && (
              <>
                <div className="form-group">
                  <label>Repeat (number of classes, max 3 months)</label>
                  <input
                    type="number"
                    min="1"
                    max={formData.repeat_interval === 'daily' ? 92 : 14}
                    value={formData.repeat_count}
                    onChange={(e) => setFormData({ ...formData, repeat_count: parseInt(e.target.value) || 1 })}
                  />
                  <small style={{ display: 'block', color: '#666', marginTop: '4px' }}>
                    {formData.repeat_interval === 'weekly' ? 'Max 14 (weekly over ~3 months)' : 'Max 92 (daily over ~3 months)'}
                  </small>
                </div>
                <div className="form-group">
                  <label>Repeat interval</label>
                  <select
                    value={formData.repeat_interval}
                    onChange={(e) => setFormData({ ...formData, repeat_interval: e.target.value })}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
              </>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" className="btn btn-primary">Create Class</button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowCreateForm(false)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {slots.length > 0 && user && isAdmin(user) && !showCreateForm && (
        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
          <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
            + Add Another Class
          </button>
        </div>
      )}

      {user && !isAdmin(user) && packageInfo != null && (
        <div className="card" style={{ marginBottom: '16px', background: 'var(--surface, #f8f9fa)', borderLeft: '4px solid var(--primary, #007bff)' }}>
          <h4 style={{ marginTop: 0, marginBottom: '8px' }}>Your credits</h4>
          <p style={{ margin: 0, color: '#333' }}>
            <strong>Credits remaining:</strong> {packageInfo.remaining}
            {packageInfo.expire_date && (
              <>
                {' · '}
                <strong>Expires:</strong> {formatExpireDate(packageInfo.expire_date)}
              </>
            )}
          </p>
          {(packageInfo.remaining === 0 || (packageInfo.expire_date && getTodayPST() > packageInfo.expire_date)) && (
            <p style={{ margin: '8px 0 0', color: 'var(--danger, #dc3545)', fontWeight: 500 }}>
              {packageInfo.remaining === 0 ? 'No credits remaining. Contact admin to get a 4-class package.' : 'Package expired. Contact admin for a new package.'}
            </p>
          )}

          {/* Upcoming classes: check-ins for future slots */}
          {(() => {
            const now = Date.now();
            const upcoming = slots
              .filter((s) => s.user_checked_in && s.start_time && new Date(s.start_time).getTime() > now)
              .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
            if (upcoming.length === 0) return null;
            return (
              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border, #dee2e6)' }}>
                <h5 style={{ margin: '0 0 8px', fontSize: '1rem' }}>Upcoming classes</h5>
                <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#333' }}>
                  {upcoming.map((slot) => (
                    <li key={slot.id} style={{ marginBottom: '6px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                      <span>
                        <strong>{slot.title || 'Class'}</strong>
                        {' — '}
                        {formatDateOnly(slot.start_time)}
                        {' · '}
                        {formatTimeOnly(slot.start_time)}
                        {slot.end_time && ` – ${formatTimeOnly(slot.end_time)}`}
                      </span>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleCancelCheckIn(slot.id)}
                        style={{ flexShrink: 0 }}
                      >
                        Cancel check-in
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}
        </div>
      )}

      {slots.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
          <h3>No classes available yet</h3>
          {user && isAdmin(user) ? (
            <div>
              <p style={{ marginBottom: '20px', color: '#666' }}>
                Click the "Create Class" button above to add your first class!
              </p>
              <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
                Create Your First Class
              </button>
            </div>
          ) : user ? (
            <p style={{ color: '#666' }}>No classes scheduled yet. Check back later.</p>
          ) : (
            <div>
              <p style={{ marginBottom: '20px', color: '#666' }}>
                Please login or register to create classes
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <a href="/login" className="btn btn-primary">Login</a>
                <a href="/register" className="btn btn-secondary">Register</a>
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="card" style={{ maxWidth: '940px', margin: '0 auto 20px', background: '#fbfdff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, color: '#334155' }}>Class Calendar ({monthYearLabel})</h3>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button type="button" className="btn btn-secondary btn-sm" style={{ padding: '6px 12px', fontSize: '14px', borderRadius: '999px' }} onClick={goToPrevMonth}>Prev</button>
                <button type="button" className="btn btn-secondary btn-sm" style={{ padding: '6px 12px', fontSize: '14px', borderRadius: '999px' }} onClick={goToCurrentMonth}>Today</button>
                <button type="button" className="btn btn-secondary btn-sm" style={{ padding: '6px 12px', fontSize: '14px', borderRadius: '999px' }} onClick={goToNextMonth}>Next</button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '6px' }}>
              {dayLabels.map((label) => (
                <div key={label} style={{ fontWeight: 600, color: '#64748b', textAlign: 'center', fontSize: '13px', paddingBottom: '2px' }}>{label}</div>
              ))}

              {Array.from({ length: monthStartDay }).map((_, idx) => (
                <div key={`blank-start-${idx}`} style={{ minHeight: '88px', borderRadius: '10px', background: '#f1f5f9' }} />
              ))}

              {Array.from({ length: monthDaysCount }).map((_, dayIndex) => {
                const dayOfMonth = dayIndex + 1;
                const dateKey = toCalendarDateKey(calendarMonth.getFullYear(), calendarMonth.getMonth(), dayOfMonth);
                const daySlots = slotsByDate[dateKey] || [];
                return (
                  <div
                    key={dateKey}
                    style={{
                      minHeight: '88px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '10px',
                      background: daySlots.length > 0 ? '#eef6ff' : '#ffffff',
                      padding: '6px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      boxShadow: daySlots.length > 0 ? '0 1px 3px rgba(37, 99, 235, 0.08)' : 'none'
                    }}
                  >
                    <div style={{ fontWeight: 600, color: '#475569', textAlign: 'right', fontSize: '12px' }}>{dayOfMonth}</div>
                    {daySlots.map((slot) => (
                      <button
                        key={slot.id}
                        type="button"
                        onClick={() => handlePickSlotFromCalendar(slot.id)}
                        style={{
                          border: selectedSlotId === slot.id
                            ? '2px solid #2563eb'
                            : slot.user_checked_in ? '1px solid #16a34a' : '1px solid #60a5fa',
                          borderRadius: '8px',
                          padding: '4px 6px',
                          background: selectedSlotId === slot.id ? '#dbeafe' : '#ffffff',
                          color: '#1e293b',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontSize: '11px',
                          lineHeight: 1.25
                        }}
                        title="Show class details"
                      >
                        <div style={{ fontWeight: 600 }}>{formatTimeOnly(slot.start_time)}</div>
                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {slot.title || 'Class'}
                        </div>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
            {!hasClassesInVisibleMonth && (
              <p style={{ marginTop: '12px', color: '#666' }}>No classes scheduled in this month.</p>
            )}
          </div>

          <div className="card">
            {!selectedSlot ? (
              <p style={{ margin: 0, color: '#666' }}>Click a class on the calendar to view details and actions.</p>
            ) : editingSlotId === selectedSlot.id ? (
              <>
                <h2>Edit Class</h2>
                <form onSubmit={handleUpdateSlot}>
                  <div className="form-group">
                    <label>Title *</label>
                    <input
                      type="text"
                      value={editFormData.title}
                      onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={editFormData.description}
                      onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Date *</label>
                    <input
                      type="date"
                      value={editFormData.date}
                      onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Start time *</label>
                    <input
                      type="time"
                      value={editFormData.start_time}
                      onChange={(e) => setEditFormData({ ...editFormData, start_time: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>End time *</label>
                    <input
                      type="time"
                      value={editFormData.end_time}
                      onChange={(e) => setEditFormData({ ...editFormData, end_time: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Max Participants</label>
                    <input
                      type="number"
                      min="1"
                      value={editFormData.max_participants}
                      onChange={(e) => setEditFormData({ ...editFormData, max_participants: parseInt(e.target.value) || 10 })}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button type="submit" className="btn btn-primary">Save</button>
                    <button type="button" className="btn btn-secondary" onClick={cancelEdit}>Cancel</button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <h3>{selectedSlot.title}</h3>
                {selectedSlot.description && <p style={{ marginBottom: '10px', color: '#666' }}>{selectedSlot.description}</p>}
                <p style={slotRowStyle}><strong>Date:</strong> {formatDateOnly(selectedSlot.start_time)}{selectedSlot.start_time && selectedSlot.end_time && new Date(selectedSlot.start_time).toDateString() !== new Date(selectedSlot.end_time).toDateString() ? ` – ${formatDateOnly(selectedSlot.end_time)}` : ''}</p>
                <p style={slotRowStyle}><strong>Time:</strong> {formatTimeOnly(selectedSlot.start_time)} – {formatTimeOnly(selectedSlot.end_time)}</p>
                <p style={slotRowStyle}><strong>Participants:</strong> {selectedSlot.current_participants || 0} / {selectedSlot.max_participants}</p>
                {user && !isAdmin(user) && (
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {selectedSlot.user_checked_in ? (
                      isMoreThan24hBefore(selectedSlot.start_time) ? (
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={() => handleCancelCheckIn(selectedSlot.id)}
                        >
                          Cancel check-in
                        </button>
                      ) : (
                        <span style={{ color: 'var(--success, #28a745)', fontWeight: 500 }}>You are checked in (cancel not allowed within 24h of class)</span>
                      )
                    ) : (
                      (() => {
                        const noCredits = packageInfo != null && packageInfo.remaining === 0;
                        const expired = packageInfo?.expire_date && getTodayPST() > packageInfo.expire_date;
                        const isPastClass = selectedSlot.start_time && new Date(selectedSlot.start_time).getTime() <= Date.now();
                        const cannotCheckIn = noCredits || expired || (selectedSlot.current_participants >= selectedSlot.max_participants) || isPastClass;
                        const reason = noCredits ? 'No credits' : expired ? 'Package expired' : selectedSlot.current_participants >= selectedSlot.max_participants ? 'Full' : isPastClass ? 'Class passed' : null;
                        return (
                          <button
                            className={reason === 'Class passed' ? 'btn btn-secondary' : 'btn btn-success'}
                            onClick={() => handleCheckIn(selectedSlot)}
                            disabled={cannotCheckIn}
                          >
                            {reason || 'Check In'}
                          </button>
                        );
                      })()
                    )}
                  </div>
                )}
                {isAdmin(user) && (
                  <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                    <button type="button" className="btn btn-primary" onClick={() => startEdit(selectedSlot)}>Edit</button>
                    <button type="button" className="btn btn-danger" onClick={() => handleDeleteSlot(selectedSlot.id)}>Delete</button>
                  </div>
                )}
                {!user && <p style={{ color: '#666', fontStyle: 'italic' }}>Please login to check in</p>}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TimeSlots;
